import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listBespokeTemplatesBespokeTemplatesGet } from '../../services/api/sdk.gen';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { useToast } from '../../hooks/useToastContext';
import { fetchRogerEmrFormSchemaVersions } from './fetchRogerEmrFormSchemaVersions';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import { bespokeApiErrorMessage } from './formSchemaUtils';
import {
  buildBespokeExportFilename,
  buildBespokeTemplatesExportBundleFromChosenTemplates,
  collectFormSchemaVersionPinsForFormTypes,
  downloadJsonFile,
  filterBespokeTemplatesForExport,
  type BespokeTemplateExportRow,
} from './bespokeTemplatesExport';
import {
  bespokeRowsByLeafKey,
  buildImportNestedGroups,
  collectLeafKeysFromGroups,
  importNestedGroupsToSelectableNestedRows,
  type ImportFormTypeGroup,
} from './bespokeTemplatesImport';

export function useBespokeTemplatesExport({
  open,
  formTypeCatalog,
  onClose,
}: {
  open: boolean;
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  onClose: () => void;
}) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [exportPrepLoading, setExportPrepLoading] = useState(false);
  const [exportLoadGeneration, setExportLoadGeneration] = useState(0);
  const [exportVersionsByFormType, setExportVersionsByFormType] = useState<
    Map<RogerDocFormType, FormSchemaVersion[]>
  >(() => new Map());
  const [exportCandidateRows, setExportCandidateRows] = useState<
    BespokeTemplateExportRow[]
  >([]);
  const [exportNestedGroups, setExportNestedGroups] = useState<
    ImportFormTypeGroup[]
  >([]);
  const [exportRowByKey, setExportRowByKey] = useState<
    Map<string, BespokeTemplateExportRow>
  >(() => new Map());
  const [exportSelectedLeafKeys, setExportSelectedLeafKeys] = useState<
    Set<string>
  >(() => new Set());
  const [exportInProgress, setExportInProgress] = useState(false);
  const exportSelectionInitGenRef = useRef(-1);

  useEffect(() => {
    if (!open || formTypeCatalog.length === 0) return;

    let cancelled = false;
    void (async () => {
      setExportPrepLoading(true);
      try {
        const m = new Map<RogerDocFormType, FormSchemaVersion[]>();
        await Promise.all(
          formTypeCatalog.map(async row => {
            const vers = await fetchRogerEmrFormSchemaVersions(
              row.canonical_visit_type_id
            );
            m.set(row.form_type as RogerDocFormType, vers);
          })
        );
        if (cancelled) return;

        const allTemplates = ((
          await listBespokeTemplatesBespokeTemplatesGet({
            throwOnError: true,
          })
        ).data ?? []) as BespokeTemplateResponse[];

        const allowed = collectFormSchemaVersionPinsForFormTypes({
          formTypes: formTypeCatalog.map(
            row => row.form_type as RogerDocFormType
          ),
          versionsByFormType: m,
        });

        const poolRows = filterBespokeTemplatesForExport({
          allTemplates,
          allowedFormSchemaVersionPinKeys: allowed,
        });

        setExportVersionsByFormType(m);
        setExportCandidateRows(poolRows);
        setExportLoadGeneration(g => g + 1);
      } catch (err) {
        console.error('Export modal: failed to load preview data:', err);
        if (!cancelled) {
          toastRef.current.error(
            'Could not prepare export',
            'Close and reopen Export, or check your connection.'
          );
          setExportVersionsByFormType(new Map());
          setExportCandidateRows([]);
        }
      } finally {
        if (!cancelled) {
          setExportPrepLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, formTypeCatalog]);

  useEffect(() => {
    if (!open) return;
    if (exportCandidateRows.length === 0) {
      exportSelectionInitGenRef.current = -1;
      setExportNestedGroups([]);
      setExportRowByKey(new Map());
      setExportSelectedLeafKeys(new Set());
      return;
    }
    const groups = buildImportNestedGroups({
      templates: exportCandidateRows,
      catalog: formTypeCatalog,
    });
    setExportNestedGroups(groups);
    const allKeys = collectLeafKeysFromGroups(groups);
    setExportRowByKey(bespokeRowsByLeafKey(exportCandidateRows));

    const isNewPool =
      exportSelectionInitGenRef.current !== exportLoadGeneration;
    if (isNewPool) {
      exportSelectionInitGenRef.current = exportLoadGeneration;
    }

    setExportSelectedLeafKeys(prev => {
      if (isNewPool) {
        return new Set(allKeys);
      }
      const next = new Set<string>();
      for (const k of allKeys) {
        if (prev.has(k)) next.add(k);
      }
      return next;
    });
  }, [
    exportCandidateRows,
    exportLoadGeneration,
    open,
    exportVersionsByFormType,
    formTypeCatalog,
  ]);

  useEffect(() => {
    if (open) return;
    setExportPrepLoading(false);
    setExportLoadGeneration(0);
    exportSelectionInitGenRef.current = -1;
    setExportVersionsByFormType(new Map());
    setExportCandidateRows([]);
    setExportNestedGroups([]);
    setExportRowByKey(new Map());
    setExportSelectedLeafKeys(new Set());
    setExportInProgress(false);
  }, [open]);

  const exportSelectableUiRows = useMemo(
    () =>
      importNestedGroupsToSelectableNestedRows(
        exportNestedGroups,
        formatSectionDropdownLabel
      ),
    [exportNestedGroups]
  );

  const exportAllLeafKeys = useMemo(
    () => collectLeafKeysFromGroups(exportNestedGroups),
    [exportNestedGroups]
  );

  const handleRunBespokeExport = useCallback(async () => {
    if (exportSelectedLeafKeys.size === 0) {
      toast.error('Nothing to export', 'Select at least one template row.');
      return;
    }

    const chosen: BespokeTemplateExportRow[] = [];
    for (const k of exportSelectedLeafKeys) {
      const row = exportRowByKey.get(k);
      if (row) chosen.push(row);
    }
    if (chosen.length === 0) {
      toast.error(
        'Nothing to export',
        'Selection did not resolve to any rows — reopen the dialog.'
      );
      return;
    }

    setExportInProgress(true);
    try {
      const bundle = buildBespokeTemplatesExportBundleFromChosenTemplates({
        templates: chosen,
      });

      downloadJsonFile(buildBespokeExportFilename({}), bundle);

      toast.success(
        'Export ready',
        bundle.templates.length === 0
          ? 'JSON downloaded (no templates in selection).'
          : `Downloaded ${bundle.templates.length} bespoke template row(s).`
      );
      onClose();
    } catch (err) {
      console.error('Bespoke template export failed:', err);
      toast.error('Export failed', bespokeApiErrorMessage(err));
    } finally {
      setExportInProgress(false);
    }
  }, [exportRowByKey, exportSelectedLeafKeys, onClose, toast]);

  return {
    exportPrepLoading,
    exportCandidateRows,
    exportSelectedLeafKeys,
    setExportSelectedLeafKeys,
    exportInProgress,
    exportSelectableUiRows,
    exportAllLeafKeys,
    handleRunBespokeExport,
  };
}
